import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import images from "../../../../assets/images";
import type { AudioTranscriptionDialogProps } from "../../../../types";
import { CustomMarkdown } from "../../../custom-markdown/custom-markdown";
import { AudioOverviewPanel } from "./audio-overview-panel";
import { InteractiveTranscriptPanel } from "./interactive-transcript-panel";
import {
  closeButtonStyles,
  dialogActionsStyles,
  markdownBodyStyles,
  MODAL_LANDSCAPE_COMPACT_QUERY,
  splitModalStyles,
} from "./styles";
import {
  getActiveSegmentIndex,
  getTranscriptTimeOffsetSec,
  inferSpeakerFromSegmentText,
  normalizeSpeakerName,
} from "../../../../utils/transcription";

export const AudioTranscriptionDialog: React.FC<
  AudioTranscriptionDialogProps
> = ({
  open,
  onClose,
  markdown,
  timed,
  currentTimeSec = 0,
  onSeekToSecond,
  registerTranscriptPlayerHost,
  audioOverview,
}) => {
  const theme = useTheme();
  const isCompactLandscape = useMediaQuery(MODAL_LANDSCAPE_COMPACT_QUERY);
  const isXsViewport = useMediaQuery(theme.breakpoints.down("sm"));
  const fullScreen = isXsViewport && !isCompactLandscape;

  const matchTranscriptToTabletHeight = useMediaQuery(
    theme.breakpoints.between("sm", "lg"),
  );
  const matchTranscriptToOverviewHeight =
    matchTranscriptToTabletHeight || isCompactLandscape;
  const overviewPanelRef = useRef<HTMLDivElement | null>(null);
  const [overviewHeightForTranscriptPx, setOverviewHeightForTranscriptPx] =
    useState<number | null>(null);

  const text = (markdown || "").trim();
  const hasTimed = !!(
    timed &&
    timed.format === "segments_v1" &&
    timed.segments?.length
  );

  const overviewTitle = audioOverview?.title?.trim() || "Lesson Audio";
  const current = Math.max(0, audioOverview?.currentTimeSec ?? currentTimeSec);
  const duration = Math.max(0, audioOverview?.durationSec ?? 0);
  const displayCurrentSec =
    duration > 0 && duration - current <= 1 ? duration : current;
  const transcriptOffsetSec = useMemo(
    () => getTranscriptTimeOffsetSec(timed, duration),
    [timed, duration],
  );
  const transcriptCurrentSec = Math.max(0, current - transcriptOffsetSec);

  const activeIdx = useMemo(
    () => getActiveSegmentIndex(timed, transcriptCurrentSec),
    [timed, transcriptCurrentSec],
  );

  const activeRowRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!open || activeIdx < 0) return;
    activeRowRef.current?.scrollIntoView({
      block: "center",
      behavior: "smooth",
    });
  }, [open, activeIdx]);

  const showDialog = open && (hasTimed || !!text);

  const activeSegment =
    activeIdx >= 0 ? timed?.segments?.[activeIdx] : timed?.segments?.[0];
  const rawOverviewName =
    activeSegment?.speaker ||
    inferSpeakerFromSegmentText(activeSegment?.text) ||
    audioOverview?.doctorName ||
    timed?.segments?.find((s) => s.speaker?.trim())?.speaker ||
    "Ahmed";
  const overviewDoctorName = normalizeSpeakerName(rawOverviewName);
  const overviewAvatar = (() => {
    const key = overviewDoctorName.toLowerCase();
    if (key === "hala") return images.OnboardingBotFatima;
    if (key === "ahmed") return images.OnboardingBotEndy;
    return images.AudioOnlyAvatar;
  })();

  const progress =
    duration > 0 ? Math.max(0, Math.min(1, current / duration)) : 0;

  const handleClose = useCallback(() => {
    if (audioOverview?.isPlaying && audioOverview.onPlayPause) {
      audioOverview.onPlayPause();
    }
    onClose();
  }, [onClose, audioOverview]);

  // Keep latest measure fn for Dialog transition + window resize (refs OK in callbacks).
  const syncOverviewHeightRef = useRef<() => void>(() => {});

  useLayoutEffect(() => {
    const sync = () => {
      if (!showDialog || !hasTimed || !matchTranscriptToOverviewHeight) {
        setOverviewHeightForTranscriptPx(null);
        return;
      }
      const node = overviewPanelRef.current;
      if (!node) return;
      const h = Math.round(node.getBoundingClientRect().height);
      setOverviewHeightForTranscriptPx(h > 0 ? h : null);
    };

    syncOverviewHeightRef.current = sync;

    if (!showDialog || !hasTimed || !matchTranscriptToOverviewHeight) {
      setOverviewHeightForTranscriptPx(null);
      return;
    }

    const onViewportChange = () => sync();
    window.addEventListener("resize", onViewportChange);
    window.addEventListener("orientationchange", onViewportChange);

    let ro: ResizeObserver | null = null;
    const attachObserver = () => {
      const el = overviewPanelRef.current;
      if (!el) return false;
      ro?.disconnect();
      ro = new ResizeObserver(sync);
      ro.observe(el);
      sync();
      return true;
    };

    let raf1 = 0;
    let raf2 = 0;
    if (!attachObserver()) {
      raf1 = requestAnimationFrame(() => {
        if (!attachObserver()) {
          raf2 = requestAnimationFrame(() => {
            attachObserver();
          });
        }
      });
    }

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      ro?.disconnect();
      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("orientationchange", onViewportChange);
      setOverviewHeightForTranscriptPx(null);
    };
  }, [
    showDialog,
    hasTimed,
    matchTranscriptToOverviewHeight,
    overviewDoctorName,
    overviewTitle,
  ]);

  const transcriptHeightMatchSx =
    matchTranscriptToOverviewHeight && overviewHeightForTranscriptPx != null
      ? {
          height: overviewHeightForTranscriptPx,
          maxHeight: overviewHeightForTranscriptPx,
        }
      : undefined;

  return (
    <Dialog
      open={showDialog}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
      scroll="paper"
      aria-labelledby="audio-transcript-dialog-title"
      PaperProps={{
        sx: isCompactLandscape
          ? {
              m: 1.5,
              maxHeight: "calc(100vh - 24px)",
              width: "calc(100% - 24px)",
              maxWidth: "min(920px, calc(100vw - 24px))",
            }
          : undefined,
      }}
      TransitionProps={{
        onEntered: () => {
          syncOverviewHeightRef.current();
          requestAnimationFrame(() => syncOverviewHeightRef.current());
        },
      }}
    >
      <DialogTitle
        id="audio-transcript-dialog-title"
        sx={
          isCompactLandscape
            ? { py: 1, px: 2, fontSize: "1rem", lineHeight: 1.3 }
            : undefined
        }
      >
        {hasTimed ? "View Transcript" : "Transcript"}
      </DialogTitle>

      <DialogContent
        dividers
        sx={isCompactLandscape ? { py: 1, px: 1.5 } : undefined}
      >
        {hasTimed && timed ? (
          <Box sx={splitModalStyles}>
            <AudioOverviewPanel
              ref={overviewPanelRef}
              overviewAvatar={overviewAvatar}
              overviewDoctorName={overviewDoctorName}
              overviewTitle={overviewTitle}
              displayCurrentSec={displayCurrentSec}
              duration={duration}
              progress={progress}
              currentTimeSec={current}
              audioUrl={audioOverview?.audioUrl}
              isPlaying={!!audioOverview?.isPlaying}
              onPlayPause={audioOverview?.onPlayPause}
              onSeekToSecond={onSeekToSecond}
              playbackRate={audioOverview?.playbackRate}
              onPlaybackRateChange={audioOverview?.onPlaybackRateChange}
            />

            <InteractiveTranscriptPanel
              timed={timed}
              activeIdx={activeIdx}
              activeRowRef={activeRowRef}
              transcriptOffsetSec={transcriptOffsetSec}
              playbackTranscriptSec={transcriptCurrentSec}
              onSeekToSecond={onSeekToSecond}
              columnSx={transcriptHeightMatchSx}
            />
          </Box>
        ) : (
          <Box sx={markdownBodyStyles}>
            <CustomMarkdown>{text}</CustomMarkdown>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={dialogActionsStyles}>
        <Button
          onClick={handleClose}
          variant="contained"
          color="primary"
          size="small"
          sx={closeButtonStyles(theme)}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
